import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import {
  AssignedClasses,
  ClassificationSelector,
} from "components/Transaction/Slider/Classifications";
import { useFormikContext } from "formik";
import { useModal } from "hooks/useModal";
import { useMemo, useState } from "react";
import { useGetAllClassificationQuery } from "store/apis/classifications";
import { ClassificationGroup } from "types/Models/classification";
import { JournalEntryTransaction } from "../JournalEntry/AddJournalEntryModal";

const FormBasedClassificationSelector = ({
  transactionIndex,
  assignedClassificationIds,
  entityId,
  onAssignClass: onAssignClassProp,
  isDisabled = false,
  onUpgradeClick,
}: {
  transactionIndex: string;
  assignedClassificationIds: string[];
  entityId: string;
  onAssignClass?: (classUuid: string, groupUuid: string) => void;
  isDisabled?: boolean;
  onUpgradeClick?: () => void;
}) => {
  const { setFieldValue } = useFormikContext<{
    transactions: JournalEntryTransaction[];
  }>();

  const { data: allClassifications = [] } = useGetAllClassificationQuery(
    { entityId },
    { skip: !entityId }
  );

  const handleAssignClass = (classUuid: string, groupUuid: string) => {
    if (onAssignClassProp) {
      onAssignClassProp(classUuid, groupUuid);
    } else {
      const currentGroup = allClassifications.find((g) => g.uuid === groupUuid);
      const classIdsFromSameGroup =
        currentGroup?.classes.map((c) => c.uuid) || [];

      const filteredClassifications = assignedClassificationIds.filter(
        (id) => !classIdsFromSameGroup.includes(id)
      );

      const newClassifications = [...filteredClassifications, classUuid];
      setFieldValue(
        `transactions[${transactionIndex}].classification_ids`,
        newClassifications
      );
    }
  };

  return (
    <ClassificationSelector
      entityId={entityId}
      assignedClassificationIds={assignedClassificationIds}
      onAssignClass={handleAssignClass}
      isDisabled={isDisabled}
      onUpgradeClick={onUpgradeClick}
    />
  );
};

const ManageClassificationModal = ({
  isOpen,
  onClose,
  transactionIndex,
  entityId,
  assignedClassificationIds,
  allClassifications,
}: {
  isOpen: boolean;
  onClose: () => void;
  transactionIndex: string;
  entityId: string;
  assignedClassificationIds: string[];
  allClassifications: ClassificationGroup[];
}) => {
  const { setFieldValue } = useFormikContext<{
    transactions: JournalEntryTransaction[];
  }>();

  // Track temporary state - only saved on "Save" button click
  const [tempClassificationIds, setTempClassificationIds] = useState<string[]>(
    []
  );
  const [isInitialized, setIsInitialized] = useState(false);

  // Reset temp state when modal opens
  if (isOpen && !isInitialized) {
    setTempClassificationIds(assignedClassificationIds);
    setIsInitialized(true);
  } else if (!isOpen && isInitialized) {
    setIsInitialized(false);
  }

  const assignedClasses = useMemo(() => {
    return allClassifications
      .map((group) => ({
        ...group,
        classes: group.classes.filter((cls) =>
          tempClassificationIds.includes(cls.uuid)
        ),
      }))
      .filter((group) => group.classes.length > 0);
  }, [allClassifications, tempClassificationIds]);

  const onRemoveClass = (classUuid: string) => {
    const newClassifications = tempClassificationIds.filter(
      (id) => id !== classUuid
    );
    setTempClassificationIds(newClassifications);
  };

  const onAssignClass = (classUuid: string, groupUuid: string) => {
    const currentGroup = allClassifications.find((g) => g.uuid === groupUuid);
    const classIdsFromSameGroup =
      currentGroup?.classes.map((c) => c.uuid) || [];

    const filteredClassifications = tempClassificationIds.filter(
      (id) => !classIdsFromSameGroup.includes(id)
    );

    const newClassifications = [...filteredClassifications, classUuid];
    setTempClassificationIds(newClassifications);
  };

  const handleSave = () => {
    console.log("Saving classification_ids", tempClassificationIds);
    setFieldValue(
      `transactions[${transactionIndex}].classification_ids`,
      tempClassificationIds
    );
    onClose();
  };

  const handleCancel = () => {
    setTempClassificationIds(assignedClassificationIds);
    onClose();
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleCancel}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Edit Classifications</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-3">
          <div className="t-w-full">
            <Label>Classifications</Label>
            <div className="t-min-h-10 t-w-full t-py-2 t-border t-rounded t-flex t-flex-wrap t-gap-4 t-items-center">
              {assignedClasses.map((classification) => {
                const fullGroup = allClassifications.find(
                  (g) => g.uuid === classification.uuid
                );
                if (!fullGroup) return null;
                return (
                  <AssignedClasses
                    key={classification.uuid}
                    assignedClass={classification}
                    fullClassificationGroup={fullGroup}
                    entityId={entityId}
                    onRemove={onRemoveClass}
                    onAssignClass={onAssignClass}
                    assignedClassificationIds={tempClassificationIds}
                  />
                );
              })}
              <FormBasedClassificationSelector
                transactionIndex={transactionIndex}
                assignedClassificationIds={tempClassificationIds}
                entityId={entityId}
                onAssignClass={onAssignClass}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button type="button" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" customType="primary" onClick={handleSave}>
            Save
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

export const FormBasedClassificationCell = ({
  transactionIndex,
  entityId,
  isDisabled = false,
  onUpgradeClick,
}: {
  transactionIndex: string;
  entityId: string;
  isDisabled?: boolean;
  onUpgradeClick?: () => void;
}) => {
  const manageModal = useModal();
  const { values } = useFormikContext<{
    transactions: JournalEntryTransaction[];
  }>();

  const transaction = values.transactions[Number(transactionIndex)];
  const assignedClassificationIds = useMemo(
    () => transaction?.classification_ids || [],
    [transaction]
  );

  const { data: allClassifications = [] } = useGetAllClassificationQuery(
    { entityId },
    { skip: !entityId }
  );

  const assignedClasses = useMemo(() => {
    return allClassifications
      .map((group) => ({
        ...group,
        classes: group.classes.filter((cls) =>
          assignedClassificationIds.includes(cls.uuid)
        ),
      }))
      .filter((group) => group.classes.length > 0);
  }, [allClassifications, assignedClassificationIds]);

  const hasClassifications = assignedClassificationIds.length > 0;

  const tooltipContent = hasClassifications && (
    <div className="t-flex t-flex-col t-gap-1">
      {assignedClasses.map((classification) => (
        <div key={classification.uuid} className="t-flex t-items-center">
          <span className="t-text-neutral-30">{classification.name}</span>:{" "}
          {classification.classes?.[0]?.name || "-"}
        </div>
      ))}

      <div className="t-flex t-items-center t-mt-3">
        <Button
          customType="link"
          size="small"
          onClick={manageModal.open}
          className="t-text-white hover:t-text-white t-underline"
        >
          Edit/Delete
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <div className="t-flex t-justify-end t-gap-2 t-items-center">
        {hasClassifications && (
          <ConditionalToolTip condition={!manageModal.isOpen && tooltipContent}>
            <span className="t-flex t-items-center t-justify-center t-w-6 t-h-6 t-rounded-full t-bg-neutral-0 t-text-caption t-text-neutral-60">
              {hasClassifications ? assignedClassificationIds.length : 0}
            </span>
          </ConditionalToolTip>
        )}
        {!hasClassifications && (
          <FormBasedClassificationSelector
            transactionIndex={transactionIndex}
            assignedClassificationIds={assignedClassificationIds}
            entityId={entityId}
            isDisabled={isDisabled}
            onUpgradeClick={onUpgradeClick}
          />
        )}
      </div>
      {hasClassifications && (
        <ManageClassificationModal
          isOpen={manageModal.isOpen}
          onClose={manageModal.close}
          transactionIndex={transactionIndex}
          entityId={entityId}
          assignedClassificationIds={assignedClassificationIds}
          allClassifications={allClassifications}
        />
      )}
    </>
  );
};
